<template>
  <div class="container-fluid px-0">
    <div class="card mb-3 mt-3 mx-2 mx-md-4">
      <!-- Card Header -->
      <div
        class="card-header p-0 position-relative mt-n3 z-index-2"
        style="height: 55px"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-3 pb-2 h-100">
          <div class="d-flex justify-content-between align-items-center px-3">
            <h6
              class="text-white text-capitalize mb-0"
              style="font-size: clamp(0.9rem, 3vw, 1.1rem)"
            >
              {{ $t("studentAffairsDashboard.statistics") }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-0">
        <!-- Loading/Error States -->
        <div v-if="isLoading" class="text-center py-4">
          <div
            class="spinner-border text-primary"
            style="width: 2rem; height: 2rem"
            role="status"
          >
            <span class="visually-hidden">{{
              $t("studentAffairsDashboard.loadingData")
            }}</span>
          </div>
          <p class="mt-2 mb-0">
            {{ $t("studentAffairsDashboard.loadingData") }}
          </p>
        </div>

        <div
          v-else-if="error"
          class="alert alert-danger mx-3 mx-md-4 mb-3 py-2"
        >
          <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <!-- Main Content -->
        <div v-else>
          <!-- Welcome Message -->
          <div class="alert alert-primary mx-3 mx-md-4 mt-2 mb-3 py-2">
            <h5
              class="alert-heading mb-1"
              style="font-size: clamp(1rem, 3.5vw, 1.25rem)"
            >
              {{ $t("studentAffairsDashboard.welcome") }}
            </h5>
            <p class="mb-0" style="font-size: clamp(0.8rem, 2.5vw, 0.95rem)">
              {{ $t("studentAffairsDashboard.welcomeSubtitle") }}
            </p>
          </div>

          <!-- Stats Cards Row - Mobile Responsive -->
          <div class="row g-2 mb-4 mx-1 mx-md-3">
            <!-- Students Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-primary shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div
                        class="text-xs font-weight-bold text-primary text-uppercase mb-1"
                      >
                        {{ $t("studentAffairsDashboard.totalStudents") }}
                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.totalStudents || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-user-plus me-1"></i>
                        {{ stats.newStudentsThisMonth || 0 }}
                        {{ $t("studentAffairsDashboard.newThisMonth") }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-users fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link
                    to="/students"
                    class="small text-primary text-decoration-none d-block mt-2"
                  >
                    {{ $t("studentAffairsDashboard.viewAll") }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Classes Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-info shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div
                        class="text-xs font-weight-bold text-info text-uppercase mb-1"
                      >
                        {{ $t("studentAffairsDashboard.totalClasses") }}
                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.totalClasses || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-check-circle me-1"></i>
                        {{ stats.activeClassesCount || 0 }}
                        {{ $t("studentAffairsDashboard.activeClasses") }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-chalkboard fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link
                    to="/classes"
                    class="small text-info text-decoration-none d-block mt-2"
                  >
                    {{ $t("studentAffairsDashboard.viewAll") }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Guardians Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-success shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div
                        class="text-xs font-weight-bold text-success text-uppercase mb-1"
                      >
                        {{ $t("studentAffairsDashboard.totalGuardians") }}
                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.totalGuardians || 0 }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-user-friends fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link
                    to="/guardians"
                    class="small text-success text-decoration-none d-block mt-2"
                  >
                    {{ $t("studentAffairsDashboard.viewAll") }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Absent Today Card (replaces Unassigned Students) -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-danger shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div
                        class="text-xs font-weight-bold text-danger text-uppercase mb-1"
                      >
                        {{ $t("studentAffairsDashboard.absentToday") }}
                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ attendanceStats.absent || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-user-clock me-1"></i>
                        {{ attendanceStats.late || 0 }}
                        {{ $t("studentAffairsDashboard.late") }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-user-times fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link
                    :to="{ name: 'attendances' }"
                    class="small text-danger text-decoration-none d-block mt-2"
                  >
                    {{ $t("studentAffairsDashboard.viewAll") }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
            
          <!-- Quick Actions and Attendance Row - Mobile Responsive -->
          <div class="row g-2 mx-1 mx-md-3">
            <!-- Quick Actions - Full width on mobile -->
            <div class="col-12 col-lg-12 mb-2">
              <div class="card shadow h-100">
                <div class="card-header bg-gradient-primary py-3">
                  <h6
                    class="m-0 font-weight-bold text-white mx-2"
                    style="font-size: clamp(0.9rem, 3vw, 1rem)"
                  >
                    {{ $t("studentAffairsDashboard.quickActions") }}
                  </h6>
                </div>
                <div class="card-body p-2">
                  <div class="row g-2">
                    <!-- Add Student -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'students.add' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-user-plus mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{ $t("studentAffairsDashboard.addNewStudent") }}
                        </span>
                      </router-link>
                    </div>

                    <!-- Add Guardian -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'guardians.create' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-user-shield mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{ $t("studentAffairsDashboard.addNewGuardian") }}
                        </span>
                      </router-link>
                    </div>

                    <!-- Record Attendance -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'attendances.record' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-calendar-check mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{ $t("studentAffairsDashboard.recordAttendance") }}
                        </span>
                      </router-link>
                    </div>

                    <!-- Add Student to Guardian -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'guardians.index' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-link mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{
                            $t("studentAffairsDashboard.linkStudentGuardian")
                          }}
                        </span>
                      </router-link>
                    </div>

                    <!-- Student Guardians -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'students.index' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-users mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{ $t("studentAffairsDashboard.studentGuardians") }}
                        </span>
                      </router-link>
                    </div>

                    <!-- Student Attendance Records -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link
                        :to="{ name: 'attendances' }"
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px"
                      >
                        <i
                          class="fas fa-clipboard-list mb-2"
                          style="font-size: 1.5rem"
                        ></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem)">
                          {{ $t("studentAffairsDashboard.attendanceRecords") }}
                        </span>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attendance Chart - Full width on mobile -->
            <div class="col-12 col-lg-12 mb-2">
              <div class="card shadow h-100">
                <div class="card-header bg-gradient-primary py-1">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <h6
                      class="m-0 font-weight-bold text-white mx-2"
                      style="font-size: clamp(0.9rem, 3vw, 1rem)"
                    >
                      {{ $t("studentAffairsDashboard.todaysAttendance") }}
                    </h6>
                    <span class="text-white mx-2">{{ formatDate(today) }}</span>
                  </div>
                </div>
                <div class="card-body p-2">
                  <div class="row g-2">
                    <div class="col-12 col-md-6 mb-3 mb-md-0">
                      <div class="card h-100">
                        <div class="card-body p-2">
                          <div style="height: 200px; width: 100%">
                            <canvas ref="attendanceChart"></canvas>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6">
                      <div
                        class="d-flex flex-column justify-content-between h-100"
                      >
                        <div class="mb-3">
                          <h6
                            class="text-center mb-3"
                            style="font-size: clamp(0.9rem, 3vw, 1rem)"
                          >
                            {{ $t("studentAffairsDashboard.attendanceStats") }}
                          </h6>
                          <div class="d-flex justify-content-between mb-2">
                            <span
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ $t("studentAffairsDashboard.present") }}
                            </span>
                            <span
                              class="fw-bold text-success"
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ attendanceStats.present }} ({{
                                attendanceStats.presentPercentage
                              }}%)
                            </span>
                          </div>
                          <div class="d-flex justify-content-between mb-2">
                            <span
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ $t("studentAffairsDashboard.absent") }}
                            </span>
                            <span
                              class="fw-bold text-danger"
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ attendanceStats.absent }} ({{
                                attendanceStats.absentPercentage
                              }}%)
                            </span>
                          </div>
                          <div class="d-flex justify-content-between">
                            <span
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ $t("studentAffairsDashboard.late") }}
                            </span>
                            <span
                              class="fw-bold text-warning"
                              style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                            >
                              {{ attendanceStats.late }} ({{
                                attendanceStats.latePercentage
                              }}%)
                            </span>
                          </div>
                        </div>
                        <router-link
                          :to="{ name: 'attendances.record' }"
                          class="btn btn-primary w-100 mt-3"
                          style="font-size: clamp(0.8rem, 2.5vw, 0.9rem)"
                        >
                          <i class="fas fa-clipboard-list me-2"></i>
                          {{ $t("studentAffairsDashboard.recordAttendance") }}
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Students and Announcements Row -->
          <div class="row g-2 mx-1 mx-md-3 mt-2">
            <!-- Recent Students - Full width -->
            <div class="col-12 mb-2">
              <div class="card shadow-sm h-100">
                <div class="card-header bg-gradient-primary py-1">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <h6
                      class="m-0 text-white mx-2"
                      style="font-size: clamp(0.8rem, 3vw, 1rem)"
                    >
                      {{ $t("studentAffairsDashboard.recentStudents") }}
                    </h6>
                    <router-link
                      to="/students"
                      class="btn btn-sm btn-link text-white mx-2"
                      style="font-size: clamp(0.7rem, 2.5vw, 0.85rem)"
                    >
                      {{ $t("studentAffairsDashboard.viewAll") }}
                    </router-link>
                  </div>
                </div>
                <div class="card-body p-1">
                  <div
                    class="table-responsive"
                    style="max-height: 300px; overflow-y: auto"
                  >
                    <table class="table table-sm align-items-center mb-0">
                      <thead>
                        <tr>
                          <th
                            class="text-uppercase text-secondary text-xs fw-bolder opacity-7"
                            style="font-size: clamp(0.6rem, 2vw, 0.75rem)"
                          >
                            {{ $t("studentAffairsDashboard.studentName") }}
                          </th>
                          <th
                            class="text-uppercase text-secondary text-xs fw-bolder opacity-7 d-none d-sm-table-cell"
                            style="font-size: clamp(0.6rem, 2vw, 0.75rem)"
                          >
                            {{ $t("studentAffairsDashboard.class") }}
                          </th>
                          <th
                            class="text-uppercase text-secondary text-xs fw-bolder opacity-7 d-none d-md-table-cell"
                            style="font-size: clamp(0.6rem, 2vw, 0.75rem)"
                          >
                            {{ $t("studentAffairsDashboard.registrationDate") }}
                          </th>
                          <th
                            class="text-uppercase text-secondary text-xs fw-bolder opacity-7"
                            style="font-size: clamp(0.6rem, 2vw, 0.75rem)"
                          >
                            {{ $t("studentAffairsDashboard.actions") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="student in recentStudents" :key="student.id">
                          <td class="align-middle">
                            <div class="d-flex align-items-center">
                              <img
                                :src="
                                  student.image_url || '/img/default-avatar.jpg'
                                "
                                class="avatar rounded-circle me-2"
                                style="
                                  width: 30px;
                                  height: 30px;
                                  object-fit: cover;
                                "
                              />
                              <div class="d-flex flex-column">
                                <span
                                  class="text-xs fw-bold mb-0"
                                  style="
                                    font-size: clamp(0.65rem, 2.5vw, 0.8rem);
                                  "
                                >
                                  {{ student.first_name }}
                                  {{ student.father_name }}
                                  {{ student.last_name }}
                                </span>
                                <span
                                  class="text-xs text-secondary d-none d-sm-inline-block"
                                  style="
                                    font-size: clamp(0.55rem, 2vw, 0.65rem);
                                  "
                                >
                                  {{ student.national_num }}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td class="align-middle d-none d-sm-table-cell">
                            <span
                              class="badge badge-sm bg-primary"
                              v-if="student.class && student.class.name"
                              style="font-size: clamp(0.55rem, 2vw, 0.65rem)"
                            >
                              {{ student.class.name }}
                            </span>
                            <span
                              class="badge badge-sm bg-secondary"
                              v-else
                              style="font-size: clamp(0.55rem, 2vw, 0.65rem)"
                            >
                              {{ $t("studentAffairsDashboard.notRegistered") }}
                            </span>
                          </td>
                          <td
                            class="align-middle text-xs d-none d-md-table-cell"
                            style="font-size: clamp(0.6rem, 2vw, 0.7rem)"
                          >
                            {{ formatDate(student.register_date) }}
                          </td>
                          <td class="align-middle">
                            <router-link
                              :to="`/students/${student.id}`"
                              class="btn btn-sm btn-outline-primary px-2"
                              style="font-size: clamp(0.6rem, 2vw, 0.7rem)"
                            >
                              {{ $t("studentAffairsDashboard.details") }}
                            </router-link>
                          </td>
                        </tr>
                        <tr v-if="recentStudents.length === 0">
                          <td colspan="4" class="text-center py-3 text-muted">
                            {{ $t("studentAffairsDashboard.noRecentStudents") }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import Chart from "chart.js/auto";
import api from "@/api";
const { t } = useI18n();
const { locale } = useI18n();
const toast = useToast();

const isLoading = ref(true);
const error = ref(null);
const attendanceChart = ref(null);
const currentAnnouncementIndex = ref(0);
let announcementInterval = null;
let chartInstance = null;

// State initialization
const stats = ref({
  totalStudents: 0,
  newStudentsThisMonth: 0,
  activeClassesCount: 0,
  totalClasses: 0,
  totalGuardians: 0,
  unassignedStudents: 0,
});

const recentStudents = ref([]);
const recentAnnouncements = ref([]);
const attendanceStats = ref({
  present: 0,
  absent: 0,
  late: 0,
  presentPercentage: 0,
  absentPercentage: 0,
  latePercentage: 0,
});

const today = new Date().toISOString().split("T")[0];

const formatDate = (dateString) => {
  if (!dateString) return "";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(locale.value, options);
};
const initAttendanceChart = () => {
  nextTick(() => {
    if (!attendanceChart.value) {
      console.error("Canvas element not found");
      return;
    }

    // Destroy previous chart if exists
    if (chartInstance) {
      chartInstance.destroy();
    }
    const ctx = attendanceChart.value.getContext("2d");
    chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: [
          t("studentAffairsDashboard.present"),
          t("studentAffairsDashboard.absent"),
          t("studentAffairsDashboard.late"),
        ],
        datasets: [
          {
            data: [
              attendanceStats.value.present || 0,
              attendanceStats.value.absent || 0,
              attendanceStats.value.late || 0,
            ],
            backgroundColor: ["#28a745", "#dc3545", "#ffc107"],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            rtl: true,
            labels: {
              font: {
                family: "Tahoma, Arial, sans-serif",
              },
              padding: 20,
            },
          },
        },
        cutout: "70%",
      },
    });
  });
};

// Announcement carousel controls
const nextAnnouncementSlide = () => {
  if (recentAnnouncements.value.length === 0) return;
  currentAnnouncementIndex.value =
    (currentAnnouncementIndex.value + 1) % recentAnnouncements.value.length;
};


// Start auto slide for announcements
const startAnnouncementAutoSlide = () => {
  if (recentAnnouncements.value.length > 1) {
    announcementInterval = setInterval(() => {
      nextAnnouncementSlide();
    }, 5000);
  }
};
function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) {
    toast.error(t("auth.unauthorized", "Unauthorized"));
    return "auth.unauthorized";
  }
  if (s === 403) {
    toast.error(t("auth.forbidden", "Forbidden"));
    return "auth.forbidden";
  }
  const msg = extractApiMessage(error, fallbackKey);
  toast.error(msg);
  return msg;
}

// Fetch dashboard data
const fetchDashboardData = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const [statsRes, studentsRes, announcementsRes, attendanceRes] =
      await Promise.all([
        api.get("/student-affairs/dashboard/stats"),
        api.get("/student-affairs/dashboard/recent-students"),
        api.get("/student-affairs/dashboard/recent-announcements"),
        api.get("/student-affairs/dashboard/today-attendance"),
      ]);

    stats.value = statsRes.data;
    recentStudents.value = studentsRes.data;
    recentAnnouncements.value = announcementsRes.data;
    attendanceStats.value = attendanceRes.data;

    await nextTick();
    initAttendanceChart();
    startAnnouncementAutoSlide();
  } catch (err) {
    console.error("Error fetching dashboard data:", err);
    notifyError(err, "studentAffairsDashboard.fetchError");
    error.value = "فشل تحميل بيانات لوحة التحكم.";
  } finally {
    isLoading.value = false;
  }
};


onMounted(async () => {
  await fetchDashboardData();
  initAttendanceChart();
});

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }
  if (announcementInterval) {
    clearInterval(announcementInterval);
  }
});
</script>

<style scoped>
/* Base Styles */
.card {
  border-radius: 0.5rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
  transition: all 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 2rem 0 rgba(58, 59, 69, 0.2);
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.border-left-primary {
  border-left: 0.25rem solid #9c27b0 !important;
}

.border-left-success {
  border-left: 0.25rem solid #2c91ae !important;
}

.border-left-info {
  border-left: 0.25rem solid #36b9cc !important;
}

.border-left-warning {
  border-left: 0.25rem solid #f6c23e !important;
}

.text-xs {
  font-size: 0.7rem;
}

.text-sm {
  font-size: 0.85rem;
}

.avatar {
  width: 36px;
  height: 36px;
  object-fit: cover;
}

.chart-container {
  position: relative;
  height: 100%;
  min-height: 200px;
}

.btn-outline-primary {
  transition: all 0.3s;
}

.btn-outline-primary:hover {
  color: white !important;
}

.text-gray-800 {
  color: #5a5c69 !important;
}

.text-gray-300 {
  color: #dddfeb !important;
}

/* Announcement Carousel Styles */
.announcement-carousel-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
  margin: 1rem 0;
  direction: rtl;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.carousel-slides {
  display: flex;
  height: 100%;
  width: 100%;
}

.carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  position: relative;
}

.announcement-card {
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: #f8f9fa;
}

.announcement-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.6);
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s;
}

.carousel-control:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%) scale(1.1);
}

.carousel-control.prev {
  right: 1rem;
}

.carousel-control.next {
  left: 1rem;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
}

.carousel-indicators button.active {
  background: white;
  transform: scale(1.2);
}

.carousel-indicators button:hover {
  transform: scale(1.2);
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .card-header h6 {
    font-size: 0.9rem !important;
  }

  .card-body {
    padding: 0.75rem !important;
  }

  .quick-actions .btn {
    padding: 0.5rem !important;
    font-size: 0.75rem !important;
  }

  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .carousel-control {
    width: 30px;
    height: 30px;
    font-size: 0.8rem;
  }

  .announcement-overlay {
    padding: 1rem;
  }

  /* Adjust quick actions grid for mobile */
  .quick-actions .col-6 {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 575.98px) {
  .stats-card .col-12 {
    margin-bottom: 0.5rem;
  }

  .carousel-wrapper {
    height: 250px;
  }

  .carousel-control {
    width: 25px;
    height: 25px;
    font-size: 0.7rem;
  }

  .announcement-title {
    font-size: 1rem !important;
  }

  /* Make quick actions buttons full width on extra small screens */
  .quick-actions .col-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  /* Adjust table columns for mobile */
  .table thead th.d-none {
    display: none !important;
  }

  .table tbody td.d-none {
    display: none !important;
  }

  /* Make avatar smaller on mobile */
  .avatar {
    width: 30px;
    height: 30px;
  }

  /* Adjust badge sizes */
  .badge {
    font-size: 0.6rem !important;
    padding: 0.25rem 0.5rem;
  }
}

/* Ensure charts are responsive */
canvas {
  max-width: 100%;
  height: auto !important;
}

/* Fix for RTL layout */
[dir="rtl"] .carousel-control.prev {
  right: auto;
  left: 1rem;
}

[dir="rtl"] .carousel-control.next {
  left: auto;
  right: 1rem;
}

/* Ensure text doesn't overflow in cards */
.card-body {
  word-wrap: break-word;
}

/* Improve button touch targets on mobile */
.btn {
  padding: 0.5rem 1rem;
  min-width: 44px;
  min-height: 44px;
}

/* Better spacing for mobile */
.mx-1 {
  margin-left: 0.25rem !important;
  margin-right: 0.25rem !important;
}

/* Adjust table font sizes for mobile */
.table td,
.table th {
  font-size: 0.8rem;
}

/* Make sure images don't overflow */
img {
  max-width: 100%;
  height: auto;
}

/* Better carousel indicators for mobile */
.carousel-indicators button {
  width: 8px;
  height: 8px;
  margin: 0 2px;
}

/* Adjust announcement overlay padding for mobile */
.announcement-overlay {
  padding: 1rem;
}

/* Better spacing for quick action buttons */
.quick-actions .btn {
  margin-bottom: 0.5rem;
}

/* Ensure all text is visible on small screens */
.text-truncate {
  white-space: normal;
}

/* Better alignment for RTL */
[dir="rtl"] .text-end {
  text-align: left !important;
}

[dir="rtl"] .text-start {
  text-align: right !important;
}

/* Fix for badge positioning in RTL */
[dir="rtl"] .position-absolute.start-0 {
  right: 0 !important;
  left: auto !important;
}

[dir="rtl"] .position-absolute.end-0 {
  left: 0 !important;
  right: auto !important;
}
.chart-container {
  position: relative;
  height: 200px; /* Fixed height */
  width: 100%;
}

canvas {
  width: 100% !important;
  height: 100% !important;
}
/* Styled announcements (match announcements.index look) */
.announcement-carousel-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
  margin: 1rem 0;
  direction: rtl;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 450px; /* similar to styled component; adjust to taste */
  overflow: hidden;
}

.carousel-slides {
  display: flex;
  height: 100%;
  width: 100%;
}

.carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  position: relative;
}

.announcement-card {
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.announcement-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.announcement-scope-container {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
  max-width: calc(100% - 40px);
}

[dir="rtl"] .announcement-scope-container {
  left: auto;
  right: 20px;
}

.announcement-scope-container .badge {
  font-size: 0.7rem;
  padding: 0.4rem 0.6rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

@media (max-width: 576px) {
  .announcement-scope-container {
    bottom: 16px;
    left: 16px;
  }
  
  [dir="rtl"] .announcement-scope-container {
    left: auto;
    right: 16px;
  }
  
  .announcement-scope-container .badge {
    font-size: 0.65rem;
    padding: 0.3rem 0.5rem;
  }
}
.announcement-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  color: #6c757d;
}

.announcement-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.announcement-content {
  max-width: 800px;
  width: 100%;
  color: #fff;
  text-align: center;
  padding: 2rem;
}

.announcement-title {
  font-size: 1.6rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.announcement-description {
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.announcement-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.announcement-date {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
}

/* Controls & indicators (same style) */
.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s;
}
.carousel-control:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%) scale(1.1);
}
.carousel-control.prev {
  right: 1rem;
}
.carousel-control.next {
  left: 1rem;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}
.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
}
.carousel-indicators button.active {
  background: #fff;
  transform: scale(1.2);
}
.carousel-indicators button:hover {
  transform: scale(1.2);
}

/* RTL control swap (if needed) */
[dir="rtl"] .carousel-control.prev {
  right: 1rem;
  left: auto;
}
[dir="rtl"] .carousel-control.next {
  left: 1rem;
  right: auto;
}

@media (max-width: 768px) {
  .carousel-wrapper {
    height: 400px;
  }
  .announcement-title {
    font-size: 1.3rem;
  }
  .announcement-description {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
}

@media (max-width: 576px) {
  .carousel-wrapper {
    height: 350px;
  }
  .announcement-badge,
  .announcement-date {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
  }
}
.announcement-scope {
  position: absolute;
  bottom: 20px;
  left: 20px; /* LTR default */
  z-index: 10;
  font-size: 0.8rem;
  padding: 0.45rem 0.65rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

[dir="rtl"] .announcement-scope {
  left: auto;
  right: 20px;
}

@media (max-width: 576px) {
  .announcement-scope {
    font-size: 0.7rem;
    padding: 0.35rem 0.5rem;
    bottom: 16px;
  }
}
</style>
